import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, X, Sparkles, PenTool, Target, Brain, Newspaper,
  FileText, BarChart3, BookOpen, Users, Zap, Shield, Clock,
  CheckCircle, Star, TrendingUp, Award, Lightbulb, Rocket,
  GraduationCap, Calendar, ChevronRight, Play
} from 'lucide-react';

const FeaturesShowcasePage = () => {
  const navigate = useNavigate();
  const [selectedFeature, setSelectedFeature] = useState(null);

  const features = [
    {
      id: 'answer-generation',
      title: 'Answer Generation',
      subtitle: 'ENGINE-1',
      icon: PenTool,
      color: 'from-blue-500 to-indigo-600',
      lightColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      path: '/mains',
      description: 'Generate UPSC-standard model answers instantly',
      fullDescription: `Our Answer Generation engine creates comprehensive, examiner-approved answers for any UPSC question.

**What it does:**
• Generates structured answers with Introduction, Body, and Conclusion
• Includes relevant current affairs with year citations
• Suggests diagrams and flowcharts where applicable
• Provides thinker/scholar references for optional subjects
• Maintains UPSC word limits (150/250/300 words)

**How to use:**
1. Go to Mains Suite → Select GS Paper or Optional
2. Enter your question or select from PYQ bank
3. Click "Generate Answer"
4. Review the model answer with all components

**Best for:** Learning answer structure, understanding examiner expectations, quick revision`,
      highlights: ['Current Affairs Integration', 'Diagram Suggestions', 'Thinker References', 'Word Limit Adherence']
    },
    {
      id: 'answer-evaluation',
      title: 'Answer Evaluation',
      subtitle: 'ENGINE-2',
      icon: Target,
      color: 'from-emerald-500 to-green-600',
      lightColor: 'bg-emerald-50',
      textColor: 'text-emerald-600',
      path: '/mains',
      description: 'Get examiner-style feedback on your answers',
      fullDescription: `Our Evaluation engine analyzes your answers like a UPSC examiner would, providing detailed feedback and scores.

**What it does:**
• Scores your answer on a 10-point scale
• Provides section-wise feedback (Intro, Body, Conclusion)
• Identifies missing points and weak areas
• Compares with ideal answer structure
• Gives specific improvement suggestions

**How to use:**
1. Write your answer (type or upload handwritten)
2. Submit for evaluation
3. Review score and detailed feedback
4. Check "Topper Notes" for expert insights

**Evaluation Criteria:**
• Content relevance and accuracy (40%)
• Structure and presentation (25%)
• Examples and data usage (20%)
• Language and expression (15%)`,
      highlights: ['10-Point Scoring', 'Section-wise Feedback', 'Handwritten Upload', 'Topper Comparison']
    },
    {
      id: 'pyq-analysis',
      title: 'PYQ Analysis',
      subtitle: 'ENGINE-3',
      icon: BarChart3,
      color: 'from-purple-500 to-violet-600',
      lightColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      path: '/pyq',
      description: 'Analyze 20+ years of previous year questions',
      fullDescription: `Deep analysis of UPSC previous year questions to identify patterns, trends, and high-probability topics.

**What it does:**
• Analyzes questions from 2001-2024
• Shows topic-wise frequency distribution
• Identifies recurring themes and patterns
• Highlights syllabus areas with increasing importance
• Predicts topic probability for upcoming exams

**Key Insights:**
• Which topics repeat every 2-3 years
• New areas UPSC has started focusing on
• Safe vs risky topics for preparation
• Year-wise difficulty progression

**How to use:**
1. Go to PYQ Analysis section
2. Select subject (GS1/GS2/GS3/GS4 or Optional)
3. View topic frequency charts
4. Click on topics for detailed question list`,
      highlights: ['20+ Years Data', 'Trend Analysis', 'Topic Frequency', 'Pattern Recognition']
    },
    {
      id: 'expected-questions',
      title: 'Trend-Based Questions',
      subtitle: 'ENGINE-4',
      icon: Lightbulb,
      color: 'from-amber-500 to-orange-600',
      lightColor: 'bg-amber-50',
      textColor: 'text-amber-600',
      path: '/mains',
      description: 'Predicted questions for upcoming exams',
      fullDescription: `Our prediction engine generates probable questions based on current affairs, syllabus gaps, and UPSC patterns.

**How predictions work:**
• Analyzes current year's major events
• Cross-references with syllabus topics
• Identifies areas not asked in recent years
• Considers UPSC's questioning style evolution
• Updates weekly with new developments

**Accuracy factors:**
• Based on 15+ years of pattern analysis
• Weighted by current affairs relevance
• Adjusted for topic rotation patterns
• Validated against actual papers

**How to use:**
1. Select subject from Mains Suite
2. Go to "Trend-Based Questions" tab
3. Practice questions by probability rank
4. Generate model answers for each`,
      highlights: ['Weekly Updates', 'Probability Ranking', 'Current Affairs Based', 'Syllabus Gap Analysis']
    },
    {
      id: 'daily-intelligence',
      title: 'Daily Intelligence',
      subtitle: 'Current Affairs',
      icon: Newspaper,
      color: 'from-rose-500 to-pink-600',
      lightColor: 'bg-rose-50',
      textColor: 'text-rose-600',
      path: '/daily-intelligence',
      description: 'Curated UPSC-relevant news daily',
      fullDescription: `Daily curated current affairs with UPSC relevance scoring and connected syllabus topics.

**Daily Digest includes:**
• 10 most important news items
• UPSC relevance score (1-10) for each
• Connected syllabus topics
• Potential question angles
• Key facts to remember

**Features:**
• Updated every morning at 6 AM IST
• Weekly compilations every Monday
• Monthly revision sheets
• Topic-wise categorization

**How to use:**
1. Check Daily Intelligence every morning
2. Note down 2-3 items for answer practice
3. Link news to static portions
4. Use weekly compilations for revision`,
      highlights: ['Daily Updates', 'Relevance Scoring', 'Syllabus Mapping', 'Weekly Compilations']
    },
    {
      id: 'editorial-analyzer',
      title: 'Editorial Analyzer',
      subtitle: 'Deep Analysis Tool',
      icon: BookOpen,
      color: 'from-orange-500 to-amber-600',
      lightColor: 'bg-orange-50',
      textColor: 'text-orange-600',
      path: '/editorial-analyzer',
      description: 'Paste any editorial, get complete UPSC analysis',
      fullDescription: `Transform any newspaper editorial into a comprehensive UPSC study material with deep editorial analysis.

**What you get:**
• 100-word & 250-word summaries
• Key facts & data points extraction
• GS Paper mapping (GS1/GS2/GS3/GS4)
• Stakeholder analysis
• Pros & Cons (5 each)
• Way Forward solutions

**Mains Preparation:**
• AI-generated Mains question
• Complete answer framework (Intro + 5 Body Points + Conclusion)
• 5 Prelims MCQs with explanations

**Legal & Policy Linkages:**
• Constitutional Articles
• Acts & Laws
• Government Schemes
• International Conventions

**How to use:**
1. Copy any editorial from The Hindu/Indian Express
2. Paste in the analyzer
3. Get complete UPSC-oriented analysis
4. Save to your library for revision`,
      highlights: ['Any Editorial', 'Mains Q&A', '5 MCQs', 'Save to Library']
    },
    {
      id: 'prelims-suite',
      title: 'Prelims Suite',
      subtitle: 'MCQ Practice',
      icon: FileText,
      color: 'from-cyan-500 to-teal-600',
      lightColor: 'bg-cyan-50',
      textColor: 'text-cyan-600',
      path: '/prelims',
      description: 'Complete Prelims preparation toolkit',
      fullDescription: `Comprehensive MCQ practice platform with adaptive difficulty and performance analytics.

**What's included:**
• 10,000+ quality MCQs
• Topic-wise and full-length tests
• Previous year questions (2011-2024)
• CSAT practice section
• Current affairs MCQs

**Smart Features:**
• Adaptive difficulty based on performance
• Weak area identification
• Detailed explanations for every question
• Performance comparison with peers
• Spaced repetition for revision

**Test Modes:**
• Practice Mode - Unlimited attempts, instant feedback
• Test Mode - Timed, exam-like conditions
• Custom Test - Choose topics and question count`,
      highlights: ['10,000+ MCQs', 'Adaptive Difficulty', 'Weak Area Analytics', 'PYQ Bank']
    },
    {
      id: 'optional-suite',
      title: 'Optional Subjects',
      subtitle: 'Specialized Prep',
      icon: BookOpen,
      color: 'from-indigo-500 to-blue-600',
      lightColor: 'bg-indigo-50',
      textColor: 'text-indigo-600',
      path: '/optional',
      description: 'Deep preparation for optional subjects',
      fullDescription: `Specialized models trained for each optional subject with thinker integration and subject-specific patterns.

**Available Optionals:**
• Political Science & IR (PSIR)
• Sociology
• Anthropology
• Public Administration
• More coming soon...

**Each Optional includes:**
• Paper 1 & Paper 2 coverage
• Subject-specific model
• Thinker/Scholar database
• 20+ years PYQ analysis
• Trend-Based Questions

**Special Features:**
• Automatic thinker citations
• Indian context prioritization
• Contemporary examples integration
• Mark-wise answer formatting`,
      highlights: ['Subject-Specific Models', 'Thinker Integration', 'Paper 1 & 2', '20+ Years PYQs']
    },
    {
      id: 'intelligence-engine',
      title: 'Intelligence Engine',
      subtitle: 'Smart Analytics',
      icon: Brain,
      color: 'from-violet-500 to-purple-600',
      lightColor: 'bg-violet-50',
      textColor: 'text-violet-600',
      path: '/intelligence',
      description: 'Powered preparation analytics',
      fullDescription: `Advanced analytics engine that tracks your preparation and predicts selection probability.

**What it tracks:**
• Answer writing scores and trends
• MCQ accuracy across topics
• Practice consistency (streaks)
• Weak area progression
• Time spent on different areas

**Key Metrics:**
• Selection Probability Score
• Safety Zone Status (Safe/Borderline/Unsafe)
• Topic-wise Readiness
• Improvement Rate

**Insights provided:**
• Personalized focus recommendations
• Risk alerts for neglected areas
• Comparison with successful candidates
• Weekly progress reports`,
      highlights: ['Selection Probability', 'Safety Zone Tracking', 'Personalized Insights', 'Progress Reports']
    },
    {
      id: 'handwritten-upload',
      title: 'Handwritten Upload',
      subtitle: 'PRO Feature',
      icon: PenTool,
      color: 'from-slate-600 to-slate-800',
      lightColor: 'bg-slate-50',
      textColor: 'text-slate-600',
      path: '/mains',
      description: 'Upload and evaluate handwritten answers',
      fullDescription: `Upload photos of your handwritten answers for evaluation - just like the real exam.

**How it works:**
1. Write your answer on paper
2. Take a clear photo
3. Upload to CLARIAS
4. System extracts text using OCR
5. Get detailed evaluation

**Tips for best results:**
• Use blue/black pen on white paper
• Ensure good lighting, no shadows
• Keep handwriting legible
• One answer per image
• Supported: JPG, PNG (max 10MB)

**Why use this:**
• Practice in exam-like conditions
• Improve handwriting speed
• Get feedback on presentation
• Build exam temperament`,
      highlights: ['OCR Technology', 'Real Exam Practice', 'Presentation Feedback', 'PRO Exclusive']
    },
    {
      id: 'revision-scheduler',
      title: 'Revision Scheduler',
      subtitle: 'Spaced Repetition',
      icon: Calendar,
      color: 'from-green-500 to-emerald-600',
      lightColor: 'bg-green-50',
      textColor: 'text-green-600',
      path: '/prelims/revision-scheduler',
      description: 'Science-backed revision planning',
      fullDescription: `Intelligent revision scheduler using spaced repetition to maximize retention.

**How it works:**
• Tracks what you've studied
• Schedules reviews at optimal intervals
• Prioritizes weak areas
• Adapts to your forgetting curve

**Revision Intervals:**
• Day 1: Initial learning
• Day 3: First review
• Day 7: Second review
• Day 14: Third review
• Day 30: Final review

**Features:**
• Daily revision targets
• Topic-wise tracking
• Streak maintenance
• Progress visualization`,
      highlights: ['Spaced Repetition', 'Forgetting Curve', 'Daily Targets', 'Progress Tracking']
    },
    {
      id: 'news-hub',
      title: 'News Hub',
      subtitle: 'Scraped Current Affairs',
      icon: Newspaper,
      color: 'from-orange-500 to-red-600',
      lightColor: 'bg-orange-50',
      textColor: 'text-orange-600',
      path: '/news-hub',
      description: 'Auto-scraped news from 30+ official sources',
      fullDescription: `Access current affairs scraped directly from official government and news sources, organized for UPSC preparation.

**What's included:**
• Weekly Digest - All news from last 7 days
• Topic-wise News - Filter by Economy, Polity, Environment, etc.
• Trending Topics - See what's hot for UPSC
• GS Paper Mapping - News organized by GS1/GS2/GS3/GS4

**News Sources (30+):**
• Government: PIB, RBI, ISRO, MoEFCC, PRS India
• Editorials: The Hindu, Indian Express
• International: Reuters, UN News, WHO, IMF
• Legal: Live Law
• Health: ICMR

**How to use:**
1. Go to News Hub from Dashboard
2. Browse by Weekly Digest, Topic, or GS Paper
3. Click any news to read the original article
4. Use for Mains answers and Prelims preparation

**Auto-Updated Daily** at 5:00 AM IST`,
      highlights: ['30+ Sources', 'Daily Updates', 'GS Paper Mapping', 'Topic Filtering']
    }
  ];

  const FeatureDetailModal = ({ feature, onClose }) => (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:max-w-lg sm:mx-4 rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-hidden shadow-2xl animate-in slide-in-from-bottom sm:zoom-in-95 duration-300">
        {/* Header */}
        <div className={`bg-gradient-to-r ${feature.color} p-5 text-white relative`}>
          <button 
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/20 rounded-xl">
              <feature.icon className="w-7 h-7" />
            </div>
            <div>
              <Badge className="bg-white/20 text-white border-0 text-xs mb-0.5">{feature.subtitle}</Badge>
              <h2 className="text-xl font-bold">{feature.title}</h2>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 sm:p-6 overflow-y-auto max-h-[calc(85vh-180px)]">
          {/* Highlights */}
          <div className="flex flex-wrap gap-2 mb-5">
            {feature.highlights.map((highlight, idx) => (
              <Badge key={idx} variant="outline" className={`${feature.textColor} border-current text-xs`}>
                <CheckCircle className="w-3 h-3 mr-1" />
                {highlight}
              </Badge>
            ))}
          </div>

          {/* Full Description - Properly formatted */}
          <div className="space-y-4 text-slate-600 text-sm">
            {feature.fullDescription.split('\n\n').map((section, idx) => {
              // Section headers like **What it does:**
              if (section.startsWith('**') && section.includes(':**')) {
                const headerEnd = section.indexOf(':**') + 3;
                const header = section.substring(2, headerEnd - 3);
                const content = section.substring(headerEnd).trim();
                
                // Check if content has bullet points
                if (content.includes('\n•')) {
                  const bullets = content.split('\n').filter(line => line.startsWith('•'));
                  return (
                    <div key={idx}>
                      <h4 className="font-bold text-slate-800 mb-2">{header}:</h4>
                      <ul className="space-y-1.5">
                        {bullets.map((bullet, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle className={`w-4 h-4 ${feature.textColor} mt-0.5 flex-shrink-0`} />
                            <span>{bullet.replace('• ', '')}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                }
                return (
                  <div key={idx}>
                    <h4 className="font-bold text-slate-800 mb-1">{header}:</h4>
                    <p>{content}</p>
                  </div>
                );
              }
              
              // Standalone bullets
              if (section.startsWith('•')) {
                const bullets = section.split('\n').filter(line => line.trim());
                return (
                  <ul key={idx} className="space-y-1.5">
                    {bullets.map((bullet, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle className={`w-4 h-4 ${feature.textColor} mt-0.5 flex-shrink-0`} />
                        <span>{bullet.replace('• ', '')}</span>
                      </li>
                    ))}
                  </ul>
                );
              }
              
              // Regular paragraph with possible bold text
              if (section.includes('**')) {
                const parts = section.split(/\*\*/);
                return (
                  <p key={idx}>
                    {parts.map((part, i) => 
                      i % 2 === 1 ? <strong key={i} className="text-slate-800">{part}</strong> : part
                    )}
                  </p>
                );
              }
              
              // Plain paragraph
              return <p key={idx}>{section}</p>;
            })}
          </div>

          {/* CTA */}
          <div className="mt-6 pt-4 border-t">
            <Button 
              onClick={() => { onClose(); navigate(feature.path); }}
              className={`w-full bg-gradient-to-r ${feature.color} hover:opacity-90`}
            >
              <Play className="w-4 h-4 mr-2" />
              Try {feature.title} Now
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-slate-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">CLARIAS Features</h1>
              <p className="text-sm text-slate-500">Explore everything CLARIAS offers</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-2 rounded-full mb-4">
            <Sparkles className="w-5 h-5" />
            <span className="font-medium">Powered by Smart Technology</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
            Your Complete UPSC Preparation Suite
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Click on any feature to learn how it can accelerate your preparation
          </p>
        </div>

        {/* 4-Engine Section */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-amber-500" />
            <h3 className="font-bold text-lg text-slate-800">The 4-Engine System</h3>
            <Badge className="bg-amber-100 text-amber-700 border-0">Core Technology</Badge>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.slice(0, 4).map((feature) => (
              <Card 
                key={feature.id}
                className="cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border-0 overflow-hidden group"
                onClick={() => setSelectedFeature(feature)}
              >
                <div className={`h-2 bg-gradient-to-r ${feature.color}`} />
                <CardContent className="p-5">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${feature.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <Badge variant="outline" className="mb-2 text-xs">{feature.subtitle}</Badge>
                  <h4 className="font-bold text-slate-900 mb-1">{feature.title}</h4>
                  <p className="text-sm text-slate-500">{feature.description}</p>
                  <div className="mt-3 flex items-center text-sm font-medium text-indigo-600 group-hover:text-indigo-700">
                    Learn more <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Other Features */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Rocket className="w-5 h-5 text-purple-500" />
            <h3 className="font-bold text-lg text-slate-800">Preparation Tools</h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.slice(4).map((feature) => (
              <Card 
                key={feature.id}
                className="cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border-0 overflow-hidden group"
                onClick={() => setSelectedFeature(feature)}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${feature.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg`}>
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Badge variant="outline" className="mb-1 text-xs">{feature.subtitle}</Badge>
                      <h4 className="font-bold text-slate-900">{feature.title}</h4>
                      <p className="text-sm text-slate-500 line-clamp-2">{feature.description}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center text-sm font-medium text-indigo-600 group-hover:text-indigo-700">
                    Learn more <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <Card className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-0">
          <CardContent className="p-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
              <div>
                <p className="text-3xl font-bold">10K+</p>
                <p className="text-indigo-200 text-sm">Practice Questions</p>
              </div>
              <div>
                <p className="text-3xl font-bold">20+</p>
                <p className="text-indigo-200 text-sm">Years PYQ Data</p>
              </div>
              <div>
                <p className="text-3xl font-bold">4</p>
                <p className="text-indigo-200 text-sm">Smart Engines</p>
              </div>
              <div>
                <p className="text-3xl font-bold">24/7</p>
                <p className="text-indigo-200 text-sm">Availability</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Feature Detail Modal */}
      {selectedFeature && (
        <FeatureDetailModal 
          feature={selectedFeature} 
          onClose={() => setSelectedFeature(null)} 
        />
      )}
    </div>
  );
};

export default FeaturesShowcasePage;
